import classNames from "classnames";
import style from "./DrugIndicationList.module.scss";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { mapDrugs } from "../../components/enhancedSearch/SearchResults/mapper/mapDrugs";
import { useDispatch, useSelector } from "react-redux";
import {
  drugSearchThunk,
  selectDrugSearchIsLoading,
  selectDrugSearchResults,
} from "../enhancedSearch";
import SearchResultItem from "../../components/enhancedSearch/SearchResults/SearchResultItem";
import {
  fetchContentAsync,
  selectAuth,
  selectCache,
  selectiDB,
  selectReferenceFiles,
  selectUXPlatform,
  selectVersion,
  selectZip,
} from "../counter/counterSlice";
import DrugExtraInformation from "./DrugExtraInformation";
import Loader from "../../components/Loader/Loader";
import { selectDitaContent } from "../../components/Content/ContentSlice";
import { getDitaContent } from "../../components/Content/ContentSlice";
import { findDrugHref } from "../../utils/findDrugHref";
import { isNullOrEmpty } from "../../utils/isNullOrEmpty";
import { getDescriptionFromNote, getDescriptionFromParagraph } from "./fetchDescription";
import FavouriteButton from "../../components/FavouriteButton/FavouriteButton";
import useToast from "../../hooks/useToast";
import {
  DRUG_INDEX_FAVOURITES_STORAGE_KEY,
  DRUG_INDEX_FAVOURITE_TYPE,
} from "../../components/FavouriteButton/favouritesService";
import useAppModernisation from "../../hooks/useAppModernisation";

const DEFAULT_FAVOURITE_TOAST_DURATION = 3000;

const DrugIndicationList = () => {
  const [pbsUrl, setPbsUrl] = useState("");
  const content = useSelector(selectDitaContent);
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const drugName = searchParams.get("drug");
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const iDB = useSelector(selectiDB);
  const referenceFiles = useSelector(selectReferenceFiles);
  const drugSearchResults = useSelector(selectDrugSearchResults);
  const isLoading = useSelector(selectDrugSearchIsLoading);
  const { isAppMode, isMobileView } = useAppModernisation();

  const [pregnancyInfo, setPregnancyInfo] = useState();
  const [pregnancyInfoNote, setPregnancyInfoNote] = useState("");
  const [breastfeedingCategory, setBreastfeedingCategory] = useState("");
  const [breastfeedingDescription, setBreastfeedingDescription] = useState("");
  const [breastfeedingNote, setBreastfeedingNote] = useState("");
  const [renalAdjustmentsHref, setRenalAdjustmentsHref] = useState();
  const [practicalUseGuidanceTitle, setPracticalUseGuidanceTitle] = useState();
  const [practicalUseGuidanceHref, setPracticalUseGuidanceHref] = useState();
  const dartSelectors = {};
  dartSelectors.auth = useSelector(selectAuth);
  dartSelectors.ccmsversion = useSelector(selectVersion);
  dartSelectors.uxPlatform = useSelector(selectUXPlatform);
  dartSelectors.cache = useSelector(selectCache);
  dartSelectors.iDB = useSelector(selectiDB);
  dartSelectors.zip = useSelector(selectZip);

  useEffect(() => {
    if (drugName === undefined || !drugName) {
      navigate("/");
      return;
    }
    dispatch(drugSearchThunk({ searchText: drugName }));
    iDB.read(referenceFiles.pbs_links).then((value) => {
      const pbsLinks = document.createElement("html");
      pbsLinks.innerHTML = value;
      setPbsUrl(
        Array.from(pbsLinks.querySelectorAll("xref")).find(
          (link) => link.innerHTML.toLowerCase() === drugName.toLowerCase().replaceAll("+", " + "),
        )?.attributes.href.value,
      );
    });

    fetchContentAsync(
      dartSelectors,
      "",
      "quicklinks/glossary_topics/g_p_" +
        drugName.toLowerCase().replaceAll(" ", "_").replaceAll("+", "_") +
        ".dita",
    ).then((pregnancyContent) => {
      const parser = new DOMParser();
      const pregnancyXml = parser.parseFromString(pregnancyContent, "text/xml");
      const descriptionText = pregnancyXml.querySelector("xref").innerHTML;
      setPregnancyInfo(
        descriptionText.replace("TGA pregnancy category ", "TGA pregnancy category: <b>") + "</b>",
      );
      getDescriptionFromNote(dartSelectors, pregnancyXml, setPregnancyInfoNote);
    });

    fetchContentAsync(
      dartSelectors,
      "",
      "quicklinks/glossary_topics/g_b_" +
        drugName.toLowerCase().replaceAll(" ", "_").replaceAll("+", "_") +
        ".dita",
    ).then((breastfeedingContent) => {
      const parser = new DOMParser();
      const breastfeedingXml = parser.parseFromString(breastfeedingContent, "text/xml");
      const category = breastfeedingXml.querySelector("xref").innerHTML;
      setBreastfeedingCategory(category);
      setBreastfeedingDescription(getDescriptionFromParagraph(breastfeedingXml));
      getDescriptionFromNote(dartSelectors, breastfeedingXml, setBreastfeedingNote);
    });

    fetchContentAsync(
      dartSelectors,
      "",
      "Antibiotic/Antimicrobial-dosage-modification-in-kidney-impairment/c_ABG_Antimicrobial-dosage-modification-in-kidney-impairment_topic_4.dita",
    ).then((kidneyContent) => {
      const parser = new DOMParser();
      const kidneyXml = parser.parseFromString(kidneyContent, "text/xml");
      for (const kidneyElement of kidneyXml.querySelectorAll("xref")) {
        if (kidneyElement.textContent.includes(`function: ${drugName.charAt(0).toUpperCase()}`)) {
          fetchContentAsync(
            dartSelectors,
            "",
            `Antibiotic/Antimicrobial-dosage-modification-in-kidney-impairment/${kidneyElement.getAttribute("href")}`,
          ).then((kidneyDetails) => {
            const parser = new DOMParser();
            const kidneyDetailsXml = parser.parseFromString(kidneyDetails, "text/xml");
            for (const kidneyElement of kidneyDetailsXml.querySelectorAll("xref")) {
              if (kidneyElement.textContent.toUpperCase() === drugName.toUpperCase()) {
                setRenalAdjustmentsHref(
                  "viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=bartonella-infections&guidelinename=auto&sectionId=c_ABG_Antimicrobial-dosage-modification-in-kidney-impairment_topic_2" +
                    kidneyElement.getAttribute("href").split("/"),
                );
              }
            }
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    const practicalInfoXml = getDitaContent("Drug_page_links.ditamap", content, dispatch);
    if (practicalInfoXml) {
      const href = findDrugHref(practicalInfoXml, drugName);
      if (href) {
        setPracticalUseGuidanceHref(href);
        let practicalInfoTitle;
        fetchContentAsync(dartSelectors, "", href).then((practicalUseGuidanceContent) => {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(practicalUseGuidanceContent, "text/xml");
          const titleFromDita = xmlDoc.querySelector("concept ditatitle");
          practicalInfoTitle = titleFromDita?.textContent;
          setPracticalUseGuidanceTitle(practicalInfoTitle);
        });
      }
    }
  }, [content, drugName]);

  const triggerToast = (added) => {
    toast.triggerToast({
      type: "info",
      primaryMessage: added ? "Added to favourites" : "Removed from favourites",
      rightText: '<a href="/favourites?tab=drug-index">View</a>',
      autoHideDuration: DEFAULT_FAVOURITE_TOAST_DURATION,
    });
  };

  if (drugName === undefined || !drugName) {
    return <></>;
  }

  let searchResults = mapDrugs(drugSearchResults);
  searchResults = searchResults.filter(
    (result) => result.title.toUpperCase() === drugName.toUpperCase(),
  );
  searchResults.sort((firstDrug, secondDrug) =>
    firstDrug.summary.localeCompare(secondDrug.summary),
  );

  let firstDrugName = () => {
    return !isNullOrEmpty(searchResults) ? searchResults[0].title : drugName.toLowerCase();
  };

  return (
    <div>
      {isLoading || !searchResults || searchResults.length === 0 ? (
        <div className={style.loader}>
          <Loader />
        </div>
      ) : (
        <div className={style.drugIndicationList}>
          <div className={classNames("size-14 bold", style.drugOverview)}>
            <div className={style.drugIconRow}>
              <div className={style.drugIconBackground}>
                <i className={classNames("tg-icons-Drug medium", style.drugIcon)}></i>
              </div>
              {!isAppMode && (
                <FavouriteButton
                  sectionTitle={drugName}
                  sectionUrl={`${pathname}${search}`}
                  type={DRUG_INDEX_FAVOURITE_TYPE}
                  triggerToast={triggerToast}
                  styling={classNames(isMobileView ? "size-36 medium" : "size-24 medium")}
                  storageKey={DRUG_INDEX_FAVOURITES_STORAGE_KEY}
                />
              )}
            </div>
            <div className={classNames("size-36 medium")}>{firstDrugName()}</div>
            <a
              className={style.pbsButton}
              href={pbsUrl}
              target="_blank"
              data-testid="pbs_button"
              rel="noreferrer"
            >
              <div outputclass="drugcodes tgl_pbs"></div>
              <span className={style.pbsText}>Pharmaceutical benefits listing</span>
              <i className={classNames("tg-icons-LeftArrow iconSize size-16", style.pbsArrow)}></i>
            </a>

            <div className={classNames("size-14 medium", style.mobileOnly)}>
              <DrugExtraInformation
                breastfeedingCategory={breastfeedingCategory}
                breastfeedingDescription={breastfeedingDescription}
                breastfeedingNote={breastfeedingNote}
                pregnancyInfo={pregnancyInfo}
                pregnancyInfoNote={pregnancyInfoNote}
                renalAdjustmentsHref={renalAdjustmentsHref}
                showCalculator={["tobramycin", "gentamicin", "amikacin"].includes(
                  drugName.toLowerCase(),
                )}
                practicalUseGuidance={practicalUseGuidanceTitle}
                practicalUseGuidanceFilePath={practicalUseGuidanceHref}
              ></DrugExtraInformation>
            </div>
            <div className={style.drugIndicationHeading}>
              <div>Drug indications</div>
              <div className={classNames("size-12 medium", style.drugCount)}>
                {searchResults.length} results
              </div>
            </div>
            <ul className={style.resultList}>
              {searchResults &&
                searchResults.length > 0 &&
                searchResults.map((result, index) => (
                  <SearchResultItem
                    isAppMode={true}
                    searchResult={result}
                    key={index}
                    showBorder={true}
                  />
                ))}
            </ul>
          </div>
          <div className={style.rightSideBar}>
            <DrugExtraInformation
              breastfeedingCategory={breastfeedingCategory}
              breastfeedingDescription={breastfeedingDescription}
              breastfeedingNote={breastfeedingNote}
              pregnancyInfo={pregnancyInfo}
              pregnancyInfoNote={pregnancyInfoNote}
              renalAdjustmentsHref={renalAdjustmentsHref}
              showCalculator={["tobramycin", "gentamicin", "amikacin"].includes(
                drugName.toLowerCase(),
              )}
              practicalUseGuidance={practicalUseGuidanceTitle}
              practicalUseGuidanceFilePath={practicalUseGuidanceHref}
            ></DrugExtraInformation>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrugIndicationList;
